import React, { useEffect, useRef, useState } from 'react';
import ProtectionLayer from './ProtectionLayer';

interface ProtectionManagerProps {
  enabled: boolean;
  protectionLayerRender?: (props: {
    isVisible: boolean;
    onRestore: () => void;
    reason: 'focus' | 'command' | 'printscreen';
  }) => React.ReactNode;
}

export default function ProtectionManager({ enabled, protectionLayerRender }: ProtectionManagerProps) {
  const [isWindowFocused, setIsWindowFocused] = useState(true);
  const [isCommandPressed, setIsCommandPressed] = useState(false);
  const [isPrintScreenPressed, setIsPrintScreenPressed] = useState(false);
  const printScreenTimeoutRef = useRef<number | null>(null);

  // Detector foco de ventana
  useEffect(() => {
    if (!enabled) return undefined;

    const handleFocus = () => setIsWindowFocused(true);
    const handleBlur = () => setIsWindowFocused(false);

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [enabled]);

  // Detectar teclas Command/Control y Print Screen
  useEffect(() => {
    if (!enabled) return undefined;

    const handleKeyDown = (evt: KeyboardEvent) => {
      if (evt.metaKey || evt.ctrlKey) {
        setIsCommandPressed(true);
      }

      // Detectar tecla Impr Pant (Print Screen) con protección contra pulsaciones rápidas
      if (evt.key === 'PrintScreen' || evt.keyCode === 44) {
        evt.preventDefault();
        evt.stopPropagation();

        // Limpiar timeout anterior si existe
        if (printScreenTimeoutRef.current) {
          clearTimeout(printScreenTimeoutRef.current);
        }

        setIsPrintScreenPressed(true);

        // Mantener la protección activa por 5 segundos
        printScreenTimeoutRef.current = setTimeout(() => {
          setIsPrintScreenPressed(false);
          printScreenTimeoutRef.current = null;
        }, 5000);
      }
    };

    const handleKeyUp = (evt: KeyboardEvent) => {
      if (!evt.metaKey && !evt.ctrlKey) {
        setIsCommandPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);

      // Limpiar timeout al desmontar
      if (printScreenTimeoutRef.current) {
        clearTimeout(printScreenTimeoutRef.current);
      }
    };
  }, [enabled]);

  // Si no está habilitado, no renderizar nada
  if (!enabled) {
    return null;
  }

  const isVisible = !isWindowFocused || isCommandPressed || isPrintScreenPressed;
  const reason = isPrintScreenPressed ? 'printscreen' : isCommandPressed ? 'command' : 'focus';

  const handleRestore = () => {
    setIsWindowFocused(true);
    setIsCommandPressed(false);
    setIsPrintScreenPressed(false);
  };

  return (
    <>
      {protectionLayerRender ? (
        protectionLayerRender({
          isVisible,
          onRestore: handleRestore,
          reason,
        })
      ) : (
        <ProtectionLayer isVisible={isVisible} reason={reason} onRestore={handleRestore} />
      )}
    </>
  );
}
